import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface SignupRequest {
  id: string;
  email: string;
  full_name: string;
  nome_guerra?: string;
  telefone?: string;
  unidade_lotacao?: string;
  matricula?: string;
  role_requested: string;
  status: 'PENDENTE' | 'APROVADO' | 'REJEITADO';
  justification?: string;
  decided_by?: string;
  decided_at?: string;
  decision_reason?: string;
  created_at: string;
  updated_at: string;
}

export const useSignupRequests = () => {
  return useQuery({
    queryKey: ['signup-requests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_signup_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SignupRequest[];
    },
  });
};

export const useCreateSignupRequest = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (requestData: {
      email: string;
      full_name: string;
      nome_guerra?: string;
      telefone?: string;
      unidade_lotacao?: string;
      matricula?: string;
      role_requested?: string;
      justification?: string;
    }) => {
      const { data, error } = await supabase
        .from('user_signup_requests')
        .insert([requestData])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Solicitação enviada",
        description: "Sua solicitação de cadastro foi enviada com sucesso! Aguarde aprovação dos administradores em até 72h.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao enviar solicitação",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useApproveSignupRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      requestId,
      decision,
      reason,
      role,
      habilidadesCompetencias,
      observacoes,
    }: {
      requestId: string;
      decision: 'APROVADO' | 'REJEITADO';
      reason?: string;
      role?: string;
      habilidadesCompetencias?: string;
      observacoes?: string;
    }) => {
      // Buscar a solicitação para obter os dados
      const { data: request, error: fetchError } = await supabase
        .from('user_signup_requests')
        .select('*')
        .eq('id', requestId)
        .single();

      if (fetchError) throw fetchError;

      let generatedPassword: string | undefined;

      // Se aprovado, criar o usuário via Edge Function (com privilégios)
      if (decision === 'APROVADO') {
        // Generate password client-side for security
        const { generateSecurePassword } = await import('@/utils/passwordGenerator');
        generatedPassword = generateSecurePassword();

        const { data: fnData, error: fnError } = await supabase.functions.invoke('create-user', {
          body: {
            email: request.email,
            fullName: request.full_name,
            role: role || request.role_requested || 'PADRAO',
            usuarioSistema: true,
            podeSerMembroEquipe: true,
            matricula: request.matricula,
            telefone: request.telefone,
            nomeGuerra: request.nome_guerra,
            unidadeLotacao: request.unidade_lotacao,
            habilidadesCompetencias,
            observacoes,
            password: generatedPassword,
          }
        });

        if (fnError) {
          let body: any = fnData;
          try {
            const resp = (fnError as any)?.context?.response;
            if (resp && typeof resp.json === 'function') {
              body = await resp.clone().json();
            }
          } catch { /* ignore */ }
          const duplicateCodes = ['MATRICULA_EXISTS', 'EMAIL_EXISTS', 'DUPLICATE_USERNAME', 'MATRICULA_SOFT_DELETED', 'EMAIL_SOFT_DELETED'];
          if (body?.code && duplicateCodes.includes(body.code)) {
            throw new Error(`Usuário já cadastrado. ${body?.error || ''}`.trim());
          }
          throw new Error(body?.error || fnError.message || 'Falha ao criar usuário');
        }
        if (!fnData || (typeof fnData === 'object' && 'success' in fnData && !fnData.success)) {
          throw new Error((fnData as any)?.error || 'Falha ao criar usuário');
        }
      }

      // Atualizar o status da solicitação
      const { data, error } = await supabase
        .from('user_signup_requests')
        .update({
          status: decision,
          decided_at: new Date().toISOString(),
          decided_by: (await supabase.auth.getUser()).data.user?.id,
          decision_reason: reason,
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;

      // Enviar e-mail de notificação (não-bloqueante)
      try {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            type: decision,
            recipientEmail: request.email,
            recipientName: request.full_name,
            ...(decision === 'APROVADO' && generatedPassword ? { password: generatedPassword, login: request.matricula } : {}),
            ...(decision === 'REJEITADO' && reason ? { reason } : {}),
          }
        });
      } catch (emailError) {
        console.warn('Falha ao enviar e-mail de notificação:', emailError);
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['signup-requests'] });
      queryClient.invalidateQueries({ queryKey: ['profiles'] });
      toast({
        title: "Solicitação processada",
        description: `Solicitação ${variables.decision.toLowerCase()} com sucesso! E-mail de notificação enviado.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao processar solicitação",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useDeleteSignupRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from("user_signup_requests")
        .delete()
        .eq("id", requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["signup-requests"] });
      toast({
        title: "Solicitação excluída",
        description: "A solicitação foi excluída com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir solicitação",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useBlockUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ email, matricula, reason }: { 
      email?: string; 
      matricula?: string; 
      reason: string; 
    }) => {
      const { error } = await supabase
        .from("user_blocks")
        .insert({
          email,
          matricula,
          reason,
          blocked_by: (await supabase.auth.getUser()).data.user?.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["signup-requests"] });
      toast({
        title: "Usuário bloqueado",
        description: "O usuário foi bloqueado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao bloquear usuário",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useCheckSignupEligibility = () => {
  return useMutation({
    mutationFn: async (data: { email: string; matricula?: string }) => {
      // Verificar se usuário está bloqueado
      const { data: isBlocked } = await supabase
        .rpc('is_user_blocked', { 
          p_email: data.email, 
          p_matricula: data.matricula 
        });

      if (isBlocked) {
        throw new Error("BLOCKED");
      }

      // Verificar se já existe solicitação pendente
      const { data: existingRequest, error: requestError } = await supabase
        .from("user_signup_requests")
        .select("id, status, created_at")
        .eq("email", data.email)
        .eq("status", "PENDENTE")
        .maybeSingle();

      if (requestError) throw requestError;

      if (existingRequest) {
        throw new Error("PENDING");
      }

      // Verificar rate limiting
      const { data: rateLimit } = await supabase
        .rpc('check_signup_rate_limit', { 
          p_email: data.email,
          p_ip_address: null // Seria ideal capturar o IP, mas por simplicidade omitimos
        });

      if (rateLimit && typeof rateLimit === 'object' && 'allowed' in rateLimit && !(rateLimit as any).allowed) {
        const error = new Error("RATE_LIMIT");
        (error as any).details = rateLimit;
        throw error;
      }

      return { allowed: true };
    },
  });
};

export const useSelfSignupSettings = () => {
  return useQuery({
    queryKey: ['self-signup-settings'],
    queryFn: async () => {
      // Use maybeSingle() instead of single() to avoid 406 errors when RLS blocks access
      // This allows the login page to load gracefully for unauthenticated users
      const { data, error } = await supabase
        .from('system_settings')
        .select('*')
        .eq('key', 'self_signup')
        .maybeSingle();

      // Gracefully handle errors - default to enabled if setting is not accessible
      if (error) {
        console.warn('Could not fetch self_signup setting:', error.message);
        return { enabled: true, admin_name: "Administrador do Sistema", admin_phone: "(00) 0000-0000", admin_location: "Administração Central" };
      }
      
      return data?.value || { enabled: true, admin_name: "Administrador do Sistema", admin_phone: "(00) 0000-0000", admin_location: "Administração Central" };
    },
    // Retry menos agressivo para evitar múltiplos erros no console
    retry: 1,
    staleTime: 30000,
  });
};

export const useReopenSignupRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const { data, error } = await supabase
        .from('user_signup_requests')
        .update({
          status: 'PENDENTE',
          decided_at: null,
          decided_by: null,
          decision_reason: null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-requests'] });
      toast({
        title: "Solicitação reaberta",
        description: "A solicitação foi reaberta e pode ser reavaliada.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Erro ao reabrir solicitação",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useLogSignupAttempt = () => {
  return useMutation({
    mutationFn: async (data: { email: string; userAgent?: string }) => {
      const { error } = await supabase
        .rpc('log_signup_attempt', {
          p_email: data.email,
          p_user_agent: data.userAgent || navigator.userAgent,
        });

      if (error) throw error;
    },
  });
};